/**
 * AssetBrowser Component
 * 
 * A modal component for browsing and selecting static assets (fonts and images)
 * for use in the WYSIWYG label editor. Supports infinite scroll pagination.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { AssetReference, StaticFile, AssetCategory } from './types';
import {
  staticFileToAssetReference,
  getAcceptAttribute,
  validateFileCategory,
  getInvalidFileMessage,
  type AssetApi,
} from './assetService';
import { useAssetBrowser } from './useAssetBrowser';

const palette = {
  pageBg: 'var(--bg)',
  cardBg: 'var(--panel)',
  cardAlt: 'var(--bg)',
  border: 'var(--border)',
  text: 'var(--primary)',
  muted: 'var(--muted)',
  accent: 'var(--brand)',
  accentDark: 'var(--brand)',
};

type AssetBrowserProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (asset: AssetReference) => void;
  category: AssetCategory;
  organizationId: number;
  api: AssetApi;
};

function AssetBrowserSession({
  isOpen,
  onClose,
  onSelect,
  category,
  organizationId,
  api,
}: AssetBrowserProps) {
  const [selectedAsset, setSelectedAsset] = useState<StaticFile | null>(null);
  const {
    assets, loading, loadingMore, uploading, error, hasMore,
    loadMore, upload, invalidate, setError,
  } = useAssetBrowser({ isOpen, category, organizationId, api });
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setSelectedAsset(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [isOpen, api, organizationId, category]);

  const handleScroll = useCallback(() => {
    if (!contentRef.current || loadingMore || !hasMore || loading) return;
    
    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMore();
    }
  }, [loadingMore, hasMore, loading, loadMore]);

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!validateFileCategory(file, category)) {
      setError(getInvalidFileMessage(category));
      return;
    }

    e.target.value = '';
    await upload(file);
  }, [category, setError, upload]);

  const handleClose = useCallback(() => {
    invalidate();
    onClose();
  }, [invalidate, onClose]);

  const handleConfirm = useCallback(() => {
    if (selectedAsset) {
      onSelect(staticFileToAssetReference(selectedAsset));
      handleClose();
    }
  }, [selectedAsset, onSelect, handleClose]);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'Enter' && selectedAsset) {
        handleConfirm();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose, selectedAsset, handleConfirm]);

  if (!isOpen) return null;

  const title = category === 'font' ? 'Select Font' : 'Select Image';
  const uploadLabel = category === 'font' ? 'Upload Font' : 'Upload Image';
  const emptyMessage = category === 'font' 
    ? 'No fonts uploaded yet. Upload a font to get started.'
    : 'No images uploaded yet. Upload an image to get started.';

  return (
    <div 
      className="asset-browser-overlay"
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
    >
      <div 
        className="asset-browser-modal"
        style={{
          background: palette.cardBg,
          borderRadius: 12,
          width: '90%',
          maxWidth: 700,
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          border: `1px solid ${palette.border}`,
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: `1px solid ${palette.border}`,
        }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: palette.text }}>
            {title}
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: palette.muted,
              padding: 4,
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Toolbar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 20px',
          borderBottom: `1px solid ${palette.border}`,
        }}>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              borderRadius: 8,
              background: palette.accent,
              color: 'white',
              fontSize: 13,
              fontWeight: 500,
              cursor: uploading ? 'wait' : 'pointer',
              opacity: uploading ? 0.7 : 1,
            }}
          >
            {uploading ? 'Uploading...' : uploadLabel}
            <input
              ref={fileInputRef}
              type="file"
              accept={getAcceptAttribute(category)}
              onChange={handleFileUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </label>
        </div>

        {/* Content */}
        <div 
          ref={contentRef}
          onScroll={handleScroll}
          style={{
            flex: 1,
            overflow: 'auto',
            padding: 20,
            minHeight: 200,
            maxHeight: 400,
          }}
        >
          {error && (
            <div style={{
              padding: '12px 16px',
              marginBottom: 16,
              borderRadius: 8,
              background: '#fee2e2',
              color: '#dc2626',
              fontSize: 13,
            }}>
              <p role="alert" style={{ margin: '0 0 8px' }}>{error}</p>
              <button
                type="button"
                onClick={loadMore}
                disabled={loading || loadingMore || uploading}
              >
                Retry loading assets
              </button>
            </div>
          )}

          {loading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 200,
              color: palette.muted,
            }}>
              Loading assets...
            </div>
          ) : error && assets.length === 0 ? null : assets.length === 0 ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 200,
              color: palette.muted,
              textAlign: 'center',
              padding: 20,
            }}>
              {emptyMessage}
            </div>
          ) : category === 'image' ? (
            // Image grid view
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: 12,
            }}>
              {assets.map((asset) => (
                <div
                  key={asset.id}
                  onClick={() => setSelectedAsset(asset)}
                  style={{
                    position: 'relative',
                    aspectRatio: '1',
                    borderRadius: 8,
                    border: `2px solid ${selectedAsset?.id === asset.id ? palette.accent : palette.border}`,
                    background: palette.cardAlt,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'border-color 0.15s',
                  }}
                >
                  {asset.resolvedUrl ? (
                    <img
                      src={asset.resolvedUrl}
                      alt={asset.displayName}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: 32, color: palette.muted }}>?</span>
                  )}
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '6px 8px',
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    fontSize: 11,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {asset.displayName}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Font list view
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {assets.map((asset) => (
                <div
                  key={asset.id}
                  onClick={() => setSelectedAsset(asset)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    borderRadius: 8,
                    border: `2px solid ${selectedAsset?.id === asset.id ? palette.accent : palette.border}`,
                    background: palette.cardAlt,
                    cursor: 'pointer',
                    transition: 'border-color 0.15s',
                  }}
                >
                  <span style={{
                    fontSize: 24,
                    fontFamily: asset.fontFamily || 'inherit',
                    color: palette.text,
                    width: 48,
                    textAlign: 'center',
                  }}>
                    Aa
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 18,
                      fontFamily: asset.fontFamily || 'inherit',
                      color: palette.text,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {asset.displayName.replace(/\.[^.]+$/, '')}
                    </div>
                    <div style={{
                      fontSize: 11,
                      color: palette.muted,
                      marginTop: 2,
                    }}>
                      {asset.mimeType}
                    </div>
                  </div>
                  {selectedAsset?.id === asset.id && (
                    <span style={{ color: palette.accent, fontSize: 18 }}>✓</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Loading more indicator */}
          {loadingMore && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 20,
              color: palette.muted,
            }}>
              Loading more...
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 12,
          padding: '16px 20px',
          borderTop: `1px solid ${palette.border}`,
        }}>
          <button
            onClick={handleClose}
            style={{
              padding: '10px 20px',
              borderRadius: 8,
              border: `1px solid ${palette.border}`,
              background: 'transparent',
              color: palette.text,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedAsset}
            style={{
              padding: '10px 20px',
              borderRadius: 8,
              border: 'none',
              background: selectedAsset ? palette.accent : palette.muted,
              color: 'white',
              fontSize: 14,
              fontWeight: 500,
              cursor: selectedAsset ? 'pointer' : 'not-allowed',
              opacity: selectedAsset ? 1 : 0.6,
            }}
          >
            Select
          </button>
        </div>
      </div>
    </div>
  );
}

// An open picker belongs to one organization and one asset category. Remount
// that session before rendering a different scope, so neither a selected asset
// nor a late list/upload response can cross into the new picker.
export function AssetBrowser(props: AssetBrowserProps) {
  if (!props.isOpen) return null;
  return <AssetBrowserSession key={`${props.organizationId}:${props.category}`} {...props} />;
}

export default AssetBrowser;
